import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { GqlService } from '@innovation-os-ng/core/core';
import { LogService } from '@innovation-os-ng/core/logger';
import {
  ControlUriWithVersion,
  ElementTypeRequestPayload,
  ElementTypeResponse,
  EntityConfigType,
  VANILLA
} from '@innovation-os-ng/core/shared';
import { EnvironmentService } from '@innovation-os-ng/core/utils';
import { Observable, ReplaySubject, Subject, combineLatest, lastValueFrom, of, take, throwError } from 'rxjs';
import { catchError, concatMap, map, retry, tap } from 'rxjs/operators';
import { EC_GQL_ID } from '../../gql-dbo-id';
import compatibleType from '../../graphql/queries/compatibleTypes';
import { MappedElementType, MappedElementTypeWithSpace, TypeWithImportCompatibility } from '../../model/elementType';

// Type compatibility check request response
interface CheckCopyCompatibilityResponse {
  data: {
    checkCopyCompatibility: Array<string>;
  };
}

const CUSTOM_TYPE_FF: string = 'elementTypes.custom.*';
const MAX_RETRIES: number = 2;

@Injectable({
  providedIn: 'root'
})
export class ElementTypeService {
  public updatedElementType: Subject<EntityConfigType> = new Subject<EntityConfigType>();

  private cachedElementTypes: {
    [spaceUri: string]: ReplaySubject<Array<EntityConfigType>>;
  } = {};

  constructor(
    private readonly http: HttpClient,
    private readonly environmentService: EnvironmentService,
    private readonly logger: LogService,
    private readonly gqlService: GqlService
  ) {}

  /**
   * Provide the all element types associated with provided space uri
   * It will search in cache and
   *
   * @param spaceUri {string}
   */
  public getAllElementTypes(spaceUri: string, freshFetch: boolean = false): Observable<Array<EntityConfigType>> {
    /* Check the provided space element types are already in cache */
    if (this.cachedElementTypes[spaceUri] && !freshFetch) {
      return this.cachedElementTypes[spaceUri].asObservable();
    }

    // Create new Reply subject for cache
    if (!this.cachedElementTypes[spaceUri]) {
      this.cachedElementTypes[spaceUri] = new ReplaySubject<Array<EntityConfigType>>(1);
    }

    //Request for space element types
    this.loadTypesElementTypes(spaceUri);

    return this.cachedElementTypes[spaceUri].asObservable();
  }

  /**
   * Provide the element types detail by element type uri and space uri
   *
   * @param elementTypeUri {string}
   * @param spaceUri {string}
   */
  public getElementType(elementTypeUri: string, spaceUri: string): Observable<EntityConfigType> {
    if (this.cachedElementTypes[spaceUri]) {
      return this.cachedElementTypes[spaceUri]
        .asObservable()
        .pipe(
          map((elementTypes: Array<EntityConfigType>) =>
            elementTypes.find((elementType: EntityConfigType) => elementType.uri === elementTypeUri)
          )
        );
    }

    // Create new Reply subject for cache
    this.cachedElementTypes[spaceUri] = new ReplaySubject<Array<EntityConfigType>>(1);

    //Request for space element types
    this.loadTypesElementTypes(spaceUri);

    return this.cachedElementTypes[spaceUri]
      .asObservable()
      .pipe(
        map((elementTypes: Array<EntityConfigType>) =>
          elementTypes.find((elementType: EntityConfigType) => elementType.uri === elementTypeUri)
        )
      );
  }

  /**
   * Retrieves detailed type information for the element types specified by their URIs within the given space
   *
   * @param {Array<string>} elementTypesUri - An array of URIs representing the element types for which detailed information is requested.
   * @param {string} spaceUri - The URI of the space containing the element types.
   * @returns { Observable<Array<EntityConfigType>> } - An Observable emitting an array of detailed type information for the specified element types within the space.
   */
  public getElementTypes(elementTypesUri: Array<string>, spaceUri: string): Observable<Array<EntityConfigType>> {
    // Use map to transform elementTypeUris array into an array of observables
    const typesDetail$: Array<Observable<EntityConfigType>> = elementTypesUri.map((uri) => this.getElementType(uri, spaceUri));
    // Use forkJoin to combine all observables into a single observable array
    return combineLatest(typesDetail$);
  }

  /**
   * Provide the mapped element types
   *
   * @param spaceUri {string}
   */
  public getMappedElementTypes(spaceUri: string): Observable<MappedElementType> {
    return this.getAllElementTypes(spaceUri).pipe(
      map((elementTypes: Array<EntityConfigType>) =>
        elementTypes.reduce((mappedElementTypes: MappedElementType, elementType: EntityConfigType) => {
          mappedElementTypes[elementType.uri] = elementType;
          return mappedElementTypes;
        }, {})
      )
    );
  }

  /**
   * Create new element type
   *
   * @param spaceUri {string} - space uri
   * @param payload { EntityConfigType } - element type data
   */
  public createElementType(spaceUri: string, payload: ElementTypeRequestPayload): Observable<EntityConfigType> {
    const url: string = this.environmentService.getRestUrl('elements-config/element-type');
    return this.http.post<ElementTypeResponse>(url, payload).pipe(
      map((response: ElementTypeResponse) => ({
        ...response,
        elementControlUris: response.elementControlUris.map((control: ControlUriWithVersion) => control.uri)
      })),
      tap({
        next: async (elementType: EntityConfigType) => {
          const cachedElementTypes: Array<EntityConfigType> = await lastValueFrom(this.cachedElementTypes[spaceUri].pipe(take(1)));
          this.cachedElementTypes[spaceUri].next([...cachedElementTypes, elementType]);
        },
        error: (error) => {
          this.logger.warn('ElementTypeService: Failed to create new element type', { error, spaceUri, payload });
        }
      })
    );
  }

  /**
   * Update Element type
   *
   * @param spaceUri {string} - space uri
   * @param payload { EntityConfigType } - update element type data
   */
  public updateElementType(spaceUri: string, elementTypeUri, payload: ElementTypeRequestPayload): Observable<EntityConfigType> {
    const url: string = this.environmentService.getRestUrl(`elements-config/element-type/${elementTypeUri}/${spaceUri}`);
    return this.http.put<ElementTypeResponse>(url, payload).pipe(
      map((response: ElementTypeResponse) => ({
        ...response,
        elementControlUris: response.elementControlUris.map((control: ControlUriWithVersion) => control.uri)
      })),

      tap({
        next: async (updatedElementType: EntityConfigType) => {
          const cachedElementTypes: Array<EntityConfigType> = await lastValueFrom(this.cachedElementTypes[spaceUri].pipe(take(1)));
          const updatedElementTypes: Array<EntityConfigType> = cachedElementTypes.map((et: EntityConfigType) => {
            if (et.uri === updatedElementType.uri) {
              return updatedElementType;
            }
            return et;
          });
          this.cachedElementTypes[spaceUri].next(updatedElementTypes);
          // Emit the updated element type
          this.updatedElementType.next(updatedElementType);
        },
        error: (error) => {
          this.logger.warn('ElementTypeService: Failed to update element type', { error, spaceUri, payload });
        }
      })
    );
  }

  /**
   * Delete element types from space
   *
   * @param spaceUri {string}
   * @param elementTypeUri { string }
   */
  public deleteElementType(elementTypeUri: string, spaceUri: string) {
    const url: string = this.environmentService.getRestUrl(`elements-config/element-type/${elementTypeUri}/${spaceUri}`);
    return this.http.delete(url).pipe(
      take(1),
      tap({
        next: async () => {
          const cachedElementTypes: Array<EntityConfigType> = await lastValueFrom(this.cachedElementTypes[spaceUri].pipe(take(1)));
          const updatedElementTypes: Array<EntityConfigType> = cachedElementTypes.filter(
            (et: EntityConfigType) => et.uri !== elementTypeUri
          );
          this.cachedElementTypes[spaceUri].next(updatedElementTypes);
        },
        error: (error) => {
          this.logger.warn('ElementTypeService: Failed to delete element type', { error, elementTypeUri, spaceUri });
        }
      })
    );
  }

  /**
   * Check the element types compatibility in between the workspaces
   *
   * @todo For now API doesn't support the typeUris param
   * @param {string}  sourceSpaceUri - From which space we are trying to import element
   * @param { string } targetSpaceUri - To which space we are trying to import element
   * @param { Array<string> } typeUris - If this method called with typeUris then API only check the type compatibility on those type otherwise it will check in all types
   * @returns {Array<string> } Provides the compatible element types uris
   */
  public checkTypeCompatibility(sourceSpaceUri: string, targetSpaceUri: string, typeUris?: Array<string>): Observable<Array<string>> {
    return new Observable((observer) => {
      this.gqlService
        .watchQuery<CheckCopyCompatibilityResponse, unknown>(
          {
            query: compatibleType,
            variables: {
              sourceSpaceUri,
              targetSpaceUri
            }
          },
          EC_GQL_ID
        )
        .subscribe({
          next: (response) => {
            observer.next(response);
            observer.complete();
          },
          error: (error) => observer.error(error),
          complete: () => observer.complete()
        });
    }).pipe(
      take(1),
      map((result: CheckCopyCompatibilityResponse) => result?.data?.checkCopyCompatibility),
      catchError((error) => {
        this.logger.warn(`ElementTypeService -> checkTypeCompatibility: Failed to check the type compatibility of space`, {
          error,
          sourceSpaceUri,
          targetSpaceUri,
          typeUris
        });
        return throwError(() => error);
      })
    );
  }

  /**
   * Provides source element types details with import compatibility property which indicate
   * that particular element type can be importable in target space uri or not
   *
   * @param {string}  sourceSpaceUri - From which space we are trying to import element
   * @param { string } targetSpaceUri - To which space we are trying to import element
   * @param { Array<string> } typeUris - If this method called with typeUris then API only check the type compatibility on those type otherwise it will check in all types
   * @returns {Array<TypeWithImportCompatibility> } Provides the element types with compatibility property
   */
  public getTypeWithCompatibility(
    sourceSpaceUri: string,
    targetSpaceUri: string,
    typeUrisToCheck?: Array<string>
  ): Observable<Array<TypeWithImportCompatibility>> {
    return this.getAllElementTypes(sourceSpaceUri).pipe(
      take(1),
      concatMap((elementTypes: Array<EntityConfigType>) => {
        // Prevent to sent type compatibility check request if element type are empty
        if (!elementTypes?.length) {
          return of([[], []]);
        }

        // Check the element type compatibility to import elements in target workspace
        return this.checkTypeCompatibility(sourceSpaceUri, targetSpaceUri, typeUrisToCheck).pipe(
          map((compatibleTypes) => [elementTypes, compatibleTypes])
        );
      }),
      map(([elementTypes, compatibleTypes]) => {
        if (!elementTypes?.length) {
          return [];
        }

        // If users want to check the compatibility in specific list of element type then
        // we only return the detail information of those types
        if (typeUrisToCheck) {
          return elementTypes
            .filter((type) => !typeUrisToCheck.includes(type.uri))
            .map((type) => ({ ...type, isCompatible: compatibleTypes.includes(type.uri) }));
        }

        //Otherwise it will return the all types with isCompatible property to identify the importable types to import
        return elementTypes.map((type) => ({ ...type, isCompatible: compatibleTypes.includes(type.uri) }));
      })
    );
  }

  /**
   * Fetch the all elements types associate with provided spaceUri and update the cache
   *
   * @param spaceUri {string}
   */
  private loadTypesElementTypes(spaceUri: string): void {
    const url: string = this.environmentService.getRestUrl(`elements-config/element-type/spaces/${spaceUri}`);
    this.http
      .get<Array<ElementTypeResponse>>(url)
      .pipe(
        take(1),
        map((elementTypes: Array<ElementTypeResponse>) => (Array.isArray(elementTypes) ? elementTypes : [])),
        map((elementTypes: Array<ElementTypeResponse>) =>
          elementTypes.map((elementType: ElementTypeResponse) => ({
            ...elementType,
            elementControlUris: elementType.elementControlUris.map((ctrl: ControlUriWithVersion) => ctrl.uri),
            // Assign default value to identify itonics type in new EC
            default: this.isDefaultType(elementType)
          }))
        )
      )
      .subscribe({
        next: (elementTypes: Array<EntityConfigType>) => {
          this.cachedElementTypes[spaceUri].next(elementTypes);
        },
        error: (error) => {
          this.logger.warn('ElementTypeService: Failed to fetch all elementTypes', { error, spaceUri });
        }
      });
  }

  /**
   * retrieve all the elementType available on the provided spaceUri list
   * map element type with spaceUri and elementTypeUri
   *
   * @param spaceUris Array<string>
   * @returns Observable<MappedElementTypeWithSpace>
   */
  public getAllElementTypeWithSpaceUris(spaceUris: Array<string>): Observable<MappedElementTypeWithSpace> {
    const url: string = this.environmentService.getRestUrl(`/elements-config/element-type/member-spaces`);

    return this.http.post<Array<EntityConfigType>>(url, { spaceUriList: spaceUris }).pipe(
      take(1),
      retry(MAX_RETRIES),
      map((elementTypes: Array<EntityConfigType>) =>
        elementTypes.reduce((mappedElementTypes: MappedElementTypeWithSpace, elementType: EntityConfigType) => {
          const spaceUri = elementType.spaceUri;
          const elementTypeUri = elementType.uri;
          if (!mappedElementTypes[spaceUri]) {
            mappedElementTypes[spaceUri] = {};
          }
          mappedElementTypes[spaceUri][elementTypeUri] = elementType;
          return mappedElementTypes;
        }, {})
      ),
      catchError((error) => {
        this.logger.warn(`ElementTypeService->getAllElementTypeWithSpaceUris: Unable to fetch element types by space uris`, {
          error,
          url,
          spaceUris
        });
        return throwError(() => error);
      })
    );
  }

  /**
   * Check if the given element type is vanilla or not
   * @param elementType
   * @returns
   */
  public isVanillaElementType(elementType: EntityConfigType): boolean {
    return elementType?.tenantUri === VANILLA || elementType?.default;
  }

  /**
   * Check the provide element type is originating from ITONICS or not
   * @param elementType
   * @returns
   */
  private isDefaultType(elementType: ElementTypeResponse): boolean {
    return elementType.featureFlag !== CUSTOM_TYPE_FF;
  }
}
